import { useEffect, useSyncExternalStore } from 'react'
import { api } from '@api'
import { useQuery } from '@tanstack/react-query'
import { secureTokenStore } from '@store'

/**
 * Authoritative subscription status for the current user.
 * Backed by the Phase 0 API contract: `GET /me/subscription`.
 */
export interface Subscription {
  isPremium: boolean
  expiresAt: string | null
  store: string | null
}

const DEFAULT_SUBSCRIPTION: Subscription = {
  isPremium: false,
  expiresAt: null,
  store: null,
}

// Premium status changes rarely (a purchase / a renewal), so we avoid
// refetching it on every screen focus. Five minutes is a reasonable window.
const SUBSCRIPTION_STALE_TIME_MS = 5 * 60 * 1000

export const SUBSCRIPTION_QUERY_KEY = ['subscription', 'me'] as const

// A payment the store accepted only makes the account Infinity once the
// RevenueCat webhook has reached the server, which can take longer than the
// paywall waits. While such an activation is awaited the status is asked again
// every 10 s, for 10 minutes at most, instead of sitting in the 5-minute cache.
const ACTIVATION_WATCH_MS = 10 * 60 * 1000
const ACTIVATION_POLL_MS = 10_000

let activationPendingUntil = 0
// Screens already open must learn that a watch has just started: the buy
// button of every one of them goes off, not only the one that took the payment.
const activationListeners = new Set<() => void>()

/** Start (or restart) the 10-minute watch after an accepted payment. */
export const markSubscriptionActivationPending = (): void => {
  activationPendingUntil = Date.now() + ACTIVATION_WATCH_MS
  activationListeners.forEach((listener) => listener())
}

export const isSubscriptionActivationPending = (): boolean => Date.now() < activationPendingUntil

const endSubscriptionActivationWatch = (): void => {
  activationPendingUntil = 0
  activationListeners.forEach((listener) => listener())
}

const subscribeToActivationWatch = (listener: () => void): (() => void) => {
  activationListeners.add(listener)
  return () => {
    activationListeners.delete(listener)
  }
}

const fetchSubscription = async (): Promise<Subscription> => {
  const { data } = await api.get<Subscription>('/me/subscription')
  return {
    isPremium: Boolean(data?.isPremium),
    expiresAt: data?.expiresAt ?? null,
    store: data?.store ?? null,
  }
}

/**
 * Full subscription query. Runs only when the user is authenticated
 * (guarded on the access token, like other authed reads in the app).
 */
export const useSubscription = () => {
  const isAuthenticated = !!secureTokenStore.getAccessToken()

  const query = useQuery<Subscription>({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: fetchSubscription,
    enabled: isAuthenticated,
    staleTime: SUBSCRIPTION_STALE_TIME_MS,
    refetchInterval: (activeQuery) =>
      !activeQuery.state.data?.isPremium && isSubscriptionActivationPending()
        ? ACTIVATION_POLL_MS
        : false,
  })

  const subscription = query.data ?? DEFAULT_SUBSCRIPTION
  const isWatchOn = useSyncExternalStore(
    subscribeToActivationWatch,
    isSubscriptionActivationPending,
  )

  // The server confirmed the payment: the watch is over. Left running, it would
  // switch the buy button off for a free account signing in on this device
  // within the 10 minutes, and keep asking the server for it every 10 s.
  useEffect(() => {
    if (subscription.isPremium && isWatchOn) {
      endSubscriptionActivationWatch()
    }
  }, [subscription.isPremium, isWatchOn])

  return {
    isPremium: subscription.isPremium,
    expiresAt: subscription.expiresAt,
    store: subscription.store,
    isLoading: query.isLoading,
    // A payment was accepted and the server has not confirmed it yet. Starting
    // the watch re-renders the screen; its end is only seen at the next render.
    isActivationPending: !subscription.isPremium && isWatchOn,
    refetch: query.refetch,
  }
}

/**
 * Convenience wrapper — the single source of truth for "is this user Premium?".
 * Use this for feature-gating (Phase 2) and paywall UI.
 */
export const usePremium = (): { isPremium: boolean; isLoading: boolean } => {
  const { isPremium, isLoading } = useSubscription()
  return { isPremium, isLoading }
}
